/**
 * プロビット解析（Probit Analysis）
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

import { linearRegression, type LinearRegressionResult } from './regression';

/**
 * 標準正規分布の累積分布関数（CDF）の逆関数の近似実装
 * プロビット変換で使用
 * 
 * この実装は、Acklam's algorithmを基にした近似式を使用
 * 精度: 約1e-6程度
 * 
 * @param p 累積確率（0 < p < 1）
 * @returns 標準正規分布の逆関数値
 */
export function inverseNormalCDF(p: number): number {
  if (p <= 0 || p >= 1) {
    throw new KeisukeAdapterError('cumulativeProbOutOfRange');
  }

  // Acklam's algorithm (正確な実装)
  // 参考: https://stackedboxes.org/2017/05/01/acklams-normal-quantile-function/
  const a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
  const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
  const d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];

  const p_low = 0.02425;
  const p_high = 1 - p_low;

  let q: number;
  let r: number;
  let result: number;

  if (p < p_low) {
    // 低確率領域 (p < 0.02425): 左裾なので負の値を返す
    q = Math.sqrt(-2 * Math.log(p));
    result = (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
              ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    return result;
  } else if (p > p_high) {
    // 高確率領域 (p > 0.97575): 右裾なので符号を反転して正の値を返す
    q = Math.sqrt(-2 * Math.log(1 - p));
    result = (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
              ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    return -result;
  } else {
    // 中央領域 (0.02425 <= p <= 0.97575)
    q = p - 0.5;
    r = q * q;
    result = q * (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) / 
                  (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
    return result;
  }
}

/**
 * 標準正規分布の累積分布関数（CDF）の近似実装
 * プロビット値からパーセンテージへの変換で使用
 * 
 * この実装は、Abramowitz and Stegunの近似式を使用
 * 精度: 約1e-6程度
 * 
 * @param z 標準正規分布の値
 * @returns 累積確率（0 < p < 1）
 */
export function normalCDF(z: number): number {
  // 負の値の場合は1から引く
  const sign = z < 0 ? -1 : 1;
  // Φ(z) = 0.5·(1 + erf(z/√2)) のため、erf の引数は |z|/√2 とする。
  // （A&S 7.1.26 は erf(x) を近似する式であり、z をそのまま渡すと √2 倍ずれる）
  const x = Math.abs(z) / Math.SQRT2;

  // Abramowitz and Stegun 7.1.26 による erf(x) の近似（最大誤差 ~1.5e-7）
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const t = 1.0 / (1.0 + p * x);
  const erf = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * erf);
}

/**
 * プロビット値からパーセンテージへの変換
 * p = Φ(Probit - 5)
 * 
 * @param probit プロビット値
 * @returns 死亡率または効果率（0-1の範囲）
 */
export function probitToPercentage(probit: number): number {
  const z = probit - 5;
  return normalCDF(z);
}

/**
 * プロビット変換
 * Probit = 5 + Φ^(-1)(p)
 * 
 * @param p 死亡率または効果率（0-1の範囲）
 * @returns プロビット値
 */
export function probitTransform(p: number): number {
  if (p <= 0 || p >= 1) {
    // 0%または100%の場合は極値を使用（実際のデータでは稀）
    if (p <= 0) {
      // 0%に近い場合、非常に小さな値を使用
      p = 0.001;
    } else {
      // 100%に近い場合、非常に大きな値を使用
      p = 0.999;
    }
  }

  return 5 + inverseNormalCDF(p);
}

/**
 * プロビット解析の結果
 */
export interface ProbitResult {
  slope: number;              // 傾き（b）
  intercept: number;          // 切片（a）
  ld50: number;              // LD50値
  ec50: number;              // EC50値
  ec90: number;              // EC90値
  ld50CI: { lower: number; upper: number };  // LD50の95%信頼区間
  ec50CI: { lower: number; upper: number };  // EC50の95%信頼区間
  ec90CI: { lower: number; upper: number };  // EC90の95%信頼区間
  regression: LinearRegressionResult;  // 線形回帰の結果
  isGLM?: boolean;            // GLM（二項分布 + プロビットリンク）を使用したかどうか
}

/**
 * プロビット解析によるLD50/EC50/EC90の計算
 * 
 * 注意: この実装は「手計算による簡易プロビット解析」です。
 * - プロビット値の計算: 正しい（Probit = 5 + Φ⁻¹(p)）
 * - 線形回帰: 正しい（Probit = a + b × log₁₀(用量)）
 * - 点推定（LD50/EC50/EC90）: 参考値として妥当
 * 
 * 制限事項:
 * - 死亡数/総数（n）を使用していないため、二項分布の分散を考慮していません
 * - 信頼区間は簡易計算のため、統計学的に正確ではありません
 * 
 * @param doses 用量（濃度）の配列
 * @param responses 死亡率または効果率（0-1の範囲）の配列
 * @returns プロビット解析の結果
 */
export function probitAnalysis(
  doses: number[],
  responses: number[]
): ProbitResult {
  if (doses.length !== responses.length || doses.length < 3) {
    throw new KeisukeAdapterError('minThreeDataPoints');
  }

  // 用量と反応率を対数変換とプロビット変換
  const logDoses = doses.map(d => Math.log10(d));
  const probits = responses.map(p => probitTransform(p));

  // 線形回帰: Probit = a + b * log10(dose)
  // 注意: この回帰は重み付けを行っていない（各データポイントを等しく扱う）
  // 統計学的に正確なプロビット解析では、二項分布の分散に基づく重み付けが必要
  const regression = linearRegression(logDoses, probits);

  const a = regression.intercept;  // 切片
  const b = regression.slope;      // 傾き

  if (Math.abs(b) < 1e-10) {
    throw new KeisukeAdapterError('slopeNearZero');
  }

  // LD50/EC50の計算: LD50/EC50 = 10^((5-a)/b)
  // プロビット値5が50%に対応
  const ld50 = Math.pow(10, (5 - a) / b);
  const ec50 = ld50;  // LD50とEC50は同じ計算式

  // EC90の計算: EC90 = 10^((probit90-a)/b)
  // プロビット値（90%に対応）を使用
  const probit90 = 5 + inverseNormalCDF(0.9);
  const ec90 = Math.pow(10, (probit90 - a) / b);

  // 信頼区間の計算（簡易版：回帰の標準誤差を使用）
  // 注意: この信頼区間は統計学的に正確ではありません
  const se = regression.standardError || 0.1;
  const tValue = 1.96;  // 95%信頼区間（自由度が十分大きい場合）

  // 信頼区間の計算（対数スケールで計算後、指数変換）
  // これは参考値であり、統計学的な信頼区間ではありません
  const ld50LogSE = se / Math.abs(b);
  const ld50LogCI = tValue * ld50LogSE;
  const ld50CI = {
    lower: Math.pow(10, Math.log10(ld50) - ld50LogCI),
    upper: Math.pow(10, Math.log10(ld50) + ld50LogCI),
  };

  const ec50CI = ld50CI;  // EC50とLD50は同じ信頼区間

  const ec90LogCI = ld50LogCI;  // 簡易的に同じ幅を使用
  const ec90CI = {
    lower: Math.pow(10, Math.log10(ec90) - ec90LogCI),
    upper: Math.pow(10, Math.log10(ec90) + ec90LogCI),
  };

  return {
    slope: b,
    intercept: a,
    ld50,
    ec50,
    ec90,
    ld50CI,
    ec50CI,
    ec90CI,
    regression,
    isGLM: false, // 簡易版（プロビット変換+線形回帰）はGLMではない
  };
}

/**
 * 二項分布のGLM（プロビットリンク）によるプロビット解析
 * 
 * この実装は、死亡数/総数（n）を使用した統計学的に正確なプロビット解析です。
 * - 分布: 二項分布
 * - リンク関数: プロビット（Probit = 5 + Φ⁻¹(p)）
 * - 推定方法: 最大尤度推定（Iteratively Reweighted Least Squares, IRLS）
 * - 信頼区間: Wald法による95%信頼区間
 * 
 * 参考文献:
 * - Finney, D.J. (1971). Probit Analysis, 3rd ed. Cambridge University Press.
 * - McCullagh, P., Nelder, J.A. (1989). Generalized Linear Models, 2nd ed. Chapman & Hall/CRC.
 * 
 * @param doses 用量（濃度）の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @returns プロビット解析の結果
 */
export function probitAnalysisGLM(
  doses: number[],
  dead: number[],
  total: number[]
): ProbitResult {
  if (doses.length !== dead.length || doses.length !== total.length || doses.length < 3) {
    throw new KeisukeAdapterError('minThreeDataPoints');
  }

  // データの検証
  for (let i = 0; i < doses.length; i++) {
    if (doses[i] <= 0) {
      throw new KeisukeAdapterError('doseMustBePositive', { row: i + 1 });
    }
    if (dead[i] < 0 || dead[i] > total[i]) {
      throw new KeisukeAdapterError('deadCountInvalid', { row: i + 1 });
    }
    if (total[i] <= 0) {
      throw new KeisukeAdapterError('totalMustBePositive', { row: i + 1 });
    }
  }

  const logDoses = doses.map(d => Math.log10(d));
  const responses = dead.map((d, i) => d / total[i]);

  // 初期値として簡易ロジット解析の結果を使用（RのGLM形式: qnorm(p) = inverseNormalCDF(p)）
  // 注意: probitTransform は Finney のスケール（Probit = 5 + Φ⁻¹(p)）なので使わない
  // GLM では qnorm(p) = Φ⁻¹(p) を直接使う
  const initialQnorms = responses.map(p => {
    // 0%または100%の場合は極値を使用
    if (p <= 0) p = 0.001;
    if (p >= 1) p = 0.999;
    return inverseNormalCDF(p);
  });
  const initialRegression = linearRegression(logDoses, initialQnorms);
  let a = initialRegression.intercept;
  let b = initialRegression.slope;

  // 最尤推定（重み付き反復再重み付け最小二乗法、IRLS）
  const maxIterations = 50;
  const tolerance = 1e-6;

  for (let iter = 0; iter < maxIterations; iter++) {
    let sumW = 0;
    let sumWX = 0;
    let sumWY = 0;
    let sumWXY = 0;
    let sumWX2 = 0;

    for (let i = 0; i < doses.length; i++) {
      const logDose = logDoses[i];
      const n = total[i];
      const y = dead[i];
      const p = y / n;

      // 線形予測子: eta = a + b * logDose (RのGLM形式: qnorm(p) = eta)
      const eta = a + b * logDose;
      // プロビットリンクの逆変換: p = Φ(eta) (RのGLMでは「5」を引かない)
      const pPredicted = normalCDF(eta);

      // 標準正規分布の密度関数: φ(eta) = (1/√(2π)) * exp(-eta²/2)
      const phiEta = Math.exp(-0.5 * eta * eta) / Math.sqrt(2 * Math.PI);
      
      // 重み: w = n * (φ(eta))² / (p * (1-p))
      // 二項分布の分散とプロビットリンクの導関数を考慮
      const weight = (n * phiEta * phiEta) / (pPredicted * (1 - pPredicted) + 1e-10);

      // 作業変数: z = eta + (y - n*p) / (n * φ(eta))
      const workingY = eta + (y - n * pPredicted) / (n * phiEta + 1e-10);

      sumW += weight;
      sumWX += weight * logDose;
      sumWY += weight * workingY;
      sumWXY += weight * logDose * workingY;
      sumWX2 += weight * logDose * logDose;
    }

    // 重み付き最小二乗法でパラメータを更新
    const det = sumW * sumWX2 - sumWX * sumWX;
    if (Math.abs(det) < 1e-10) {
      break; // 行列が特異
    }

    const aNew = (sumWY * sumWX2 - sumWXY * sumWX) / det;
    const bNew = (sumW * sumWXY - sumWX * sumWY) / det;

    // 収束判定
    if (Math.abs(aNew - a) < tolerance && Math.abs(bNew - b) < tolerance) {
      a = aNew;
      b = bNew;
      break;
    }

    a = aNew;
    b = bNew;
  }

  if (Math.abs(b) < 1e-10) {
    throw new KeisukeAdapterError('slopeNearZero');
  }

  // LD50/EC50の計算 (RのGLM形式: qnorm(0.5) = 0 なので 0 = a + b * x50 => x50 = -a/b)
  const x50 = -a / b;
  const ld50 = Math.pow(10, x50);
  const ec50 = ld50;

  // EC90の計算 (qnorm(0.9) = 1.281552 なので 1.281552 = a + b * x90 => x90 = (1.281552 - a)/b)
  const qnorm90 = inverseNormalCDF(0.9); // ≈ 1.281552
  const x90 = (qnorm90 - a) / b;
  const ec90 = Math.pow(10, x90);

  // 最適な負の対数尤度を計算（LD50のProfile Likelihood CI用）
  const nllOptimal = -logLikelihoodProbit(a, b, logDoses, dead, total);

  // LD50そのもののProfile Likelihood CIを計算（再パラメータ化を使用）
  const x50CI = calculateLogDoseEffectProfileCI(
    x50,
    0,
    logDoses,
    dead,
    total,
    b,
    nllOptimal
  );
  
  const ld50CI = {
    lower: Math.pow(10, x50CI.lower),
    upper: Math.pow(10, x50CI.upper),
  };

  const ec50CI = ld50CI;

  const x90CI = calculateLogDoseEffectProfileCI(
    x90,
    qnorm90,
    logDoses,
    dead,
    total,
    b,
    nllOptimal
  );

  const ec90CI = {
    lower: Math.pow(10, x90CI.lower),
    upper: Math.pow(10, x90CI.upper),
  };

  // パラメータの分散共分散行列を計算（回帰結果用）
  let sumW = 0;
  let sumWX = 0;
  let sumWX2 = 0;

  for (let i = 0; i < doses.length; i++) {
    const logDose = logDoses[i];
    const n = total[i];
    const eta = a + b * logDose;
    const pPredicted = normalCDF(eta); // RのGLM形式（「5」を引かない）
    const phiEta = Math.exp(-0.5 * eta * eta) / Math.sqrt(2 * Math.PI);
    const weight = (n * phiEta * phiEta) / (pPredicted * (1 - pPredicted) + 1e-10);

    sumW += weight;
    sumWX += weight * logDose;
    sumWX2 += weight * logDose * logDose;
  }

  const det = sumW * sumWX2 - sumWX * sumWX;
  const varA = sumWX2 / det;
  const varB = sumW / det;
  // LD50の標準誤差: x50 = -a/b なので、Delta法により計算
  const ld50LogSE = Math.sqrt(Math.max(0, (1 / (b * b)) * (varA + Math.pow(x50, 2) * varB)));

  // 回帰結果
  const regression: LinearRegressionResult = {
    slope: b,
    intercept: a,
    rSquared: 0, // GLMではR²は定義が異なるため、ここでは0とする
    n: doses.length,
    standardError: ld50LogSE,
    slopeSE: Math.sqrt(varB),
    interceptSE: Math.sqrt(varA),
  };

  return {
    slope: b,
    intercept: a,
    ld50,
    ec50,
    ec90,
    ld50CI,
    ec50CI,
    ec90CI,
    regression,
    isGLM: true, // GLMを使用
  };
}

/**
 * 二項分布GLM（プロビットリンク）の対数尤度関数
 * 
 * log L = Σ [y_i * log(p_i) + (n_i - y_i) * log(1 - p_i)]
 * ここで p_i = Φ(a + b * log10(dose_i)) (RのGLM形式、「5」を引かない)
 * 
 * @param a 切片
 * @param b 傾き
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @returns 対数尤度
 */
function logLikelihoodProbit(
  a: number,
  b: number,
  logDoses: number[],
  dead: number[],
  total: number[]
): number {
  let logLik = 0;
  
  for (let i = 0; i < logDoses.length; i++) {
    const eta = a + b * logDoses[i];
    const p = normalCDF(eta); // RのGLM形式（「5」を引かない）
    
    // 数値安定性のため、pが0や1に近い場合の処理
    const pSafe = Math.max(1e-10, Math.min(1 - 1e-10, p));
    
    const y = dead[i];
    const n = total[i];
    
    logLik += y * Math.log(pSafe) + (n - y) * Math.log(1 - pSafe);
  }
  
  return logLik;
}

/**
 * 切片を固定して傾きを最適化（対数尤度を最大化）
 * 
 * @param aFixed 固定する切片の値
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @param bInitial 初期傾きの推定値
 * @returns 最適化された傾きと対数尤度
 */
function optimizeSlopeGivenIntercept(
  aFixed: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  bInitial: number
): { b: number; logLik: number } {
  let b = bInitial;
  const maxIterations = 50;
  const tolerance = 1e-6;
  const stepSize = 0.01;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    // 数値微分で勾配を計算
    const logLikCurrent = logLikelihoodProbit(aFixed, b, logDoses, dead, total);
    const logLikPlus = logLikelihoodProbit(aFixed, b + stepSize, logDoses, dead, total);
    const gradient = (logLikPlus - logLikCurrent) / stepSize;
    
    // 勾配が0に近い場合は終了
    if (Math.abs(gradient) < tolerance) {
      break;
    }
    
    // 勾配上昇法（尤度を最大化）
    const bNew = b + stepSize * gradient;
    
    // 収束判定
    if (Math.abs(bNew - b) < tolerance) {
      b = bNew;
      break;
    }
    
    b = bNew;
    
    // bが0に近づかないようにする
    if (Math.abs(b) < 1e-10) {
      b = bInitial;
      break;
    }
  }
  
  const logLik = logLikelihoodProbit(aFixed, b, logDoses, dead, total);
  return { b, logLik };
}

/**
 * 傾きを固定して切片を最適化（対数尤度を最大化）
 * 
 * @param bFixed 固定する傾きの値
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @param aInitial 初期切片の推定値
 * @returns 最適化された切片と対数尤度
 */
function optimizeInterceptGivenSlope(
  bFixed: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  aInitial: number
): { a: number; logLik: number } {
  let a = aInitial;
  const maxIterations = 50;
  const tolerance = 1e-6;
  const stepSize = 0.01;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    // 数値微分で勾配を計算
    const logLikCurrent = logLikelihoodProbit(a, bFixed, logDoses, dead, total);
    const logLikPlus = logLikelihoodProbit(a + stepSize, bFixed, logDoses, dead, total);
    const gradient = (logLikPlus - logLikCurrent) / stepSize;
    
    // 勾配が0に近い場合は終了
    if (Math.abs(gradient) < tolerance) {
      break;
    }
    
    // 勾配上昇法（尤度を最大化）
    const aNew = a + stepSize * gradient;
    
    // 収束判定
    if (Math.abs(aNew - a) < tolerance) {
      a = aNew;
      break;
    }
    
    a = aNew;
  }
  
  const logLik = logLikelihoodProbit(a, bFixed, logDoses, dead, total);
  return { a, logLik };
}

/**
 * Profile Likelihood CIを計算（二分探索を使用）
 * 
 * 95%信頼区間の場合、カイ二乗分布の臨界値は約3.84（自由度1）
 * 対数尤度比 = 2 * (logLik_optimal - logLik_profile) が3.84になる点を探索
 * 
 * @param paramOptimal 最適パラメータ値
 * @param optimizeFunc パラメータを固定して他のパラメータを最適化する関数
 * @param logLikOptimal 最適対数尤度
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @param otherParamOptimal 他のパラメータの最適値
 * @param isIntercept trueの場合は切片、falseの場合は傾き
 * @returns 信頼区間（下限、上限）
 */
function calculateProfileLikelihoodCI(
  paramOptimal: number,
  optimizeFunc: (paramFixed: number, logDoses: number[], dead: number[], total: number[], otherParamInitial: number) => { [key: string]: number; logLik: number },
  logLikOptimal: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  otherParamOptimal: number,
  isIntercept: boolean
): { lower: number; upper: number } {
  const chi2Critical = 3.841459; // 95%信頼区間、自由度1
  const targetLogLikDiff = chi2Critical / 2;
  const tolerance = 1e-4;
  const maxIterations = 100;
  
  // 下限を探索
  let lowerBound = paramOptimal;
  let step = Math.abs(paramOptimal) * 0.1;
  if (step < 0.1) step = 0.1;
  
  // 下限の初期探索
  for (let i = 0; i < 20; i++) {
    const paramTest = paramOptimal - step * (i + 1);
    const result = optimizeFunc(paramTest, logDoses, dead, total, otherParamOptimal);
    const logLikDiff = logLikOptimal - result.logLik;
    
    if (logLikDiff > targetLogLikDiff) {
      lowerBound = paramTest;
      break;
    }
  }
  
  // 二分探索で下限を精密化
  let lowerLow = lowerBound - step;
  let lowerHigh = paramOptimal;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    const lowerMid = (lowerLow + lowerHigh) / 2;
    const result = optimizeFunc(lowerMid, logDoses, dead, total, otherParamOptimal);
    const logLikDiff = logLikOptimal - result.logLik;
    
    if (Math.abs(logLikDiff - targetLogLikDiff) < tolerance) {
      lowerBound = lowerMid;
      break;
    }
    
    if (logLikDiff < targetLogLikDiff) {
      lowerHigh = lowerMid;
    } else {
      lowerLow = lowerMid;
    }
    
    if (Math.abs(lowerHigh - lowerLow) < tolerance) {
      break;
    }
  }
  
  // 上限を探索
  let upperBound = paramOptimal;
  
  // 上限の初期探索
  for (let i = 0; i < 20; i++) {
    const paramTest = paramOptimal + step * (i + 1);
    const result = optimizeFunc(paramTest, logDoses, dead, total, otherParamOptimal);
    const logLikDiff = logLikOptimal - result.logLik;
    
    if (logLikDiff > targetLogLikDiff) {
      upperBound = paramTest;
      break;
    }
  }
  
  // 二分探索で上限を精密化
  let upperLow = paramOptimal;
  let upperHigh = upperBound + step;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    const upperMid = (upperLow + upperHigh) / 2;
    const result = optimizeFunc(upperMid, logDoses, dead, total, otherParamOptimal);
    const logLikDiff = logLikOptimal - result.logLik;
    
    if (Math.abs(logLikDiff - targetLogLikDiff) < tolerance) {
      upperBound = upperMid;
      break;
    }
    
    if (logLikDiff < targetLogLikDiff) {
      upperLow = upperMid;
    } else {
      upperHigh = upperMid;
    }
    
    if (Math.abs(upperHigh - upperLow) < tolerance) {
      break;
    }
  }
  
  return { lower: lowerBound, upper: upperBound };
}

/**
 * 再パラメータ化した負の対数尤度関数（x50固定、b1を推定）
 * 
 * 再パラメータ化: eta = b1 * (x - x50) = b1*x - b1*x50
 * つまり β₀ = -b1*x50, β₁ = b1
 * 
 * @param b1 傾きパラメータ
 * @param x50 固定するLD50の対数値（log10(LD50)）
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @returns 負の対数尤度
 */
function negativeLogLikelihoodGivenX50(
  b1: number,
  x50: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  qEffect = 0
): number {
  let logLik = 0;
  
  for (let i = 0; i < logDoses.length; i++) {
    // 再パラメータ化: eta = qEffect + b1 * (x - xEffect)
    const eta = qEffect + b1 * (logDoses[i] - x50);
    const p = normalCDF(eta);
    
    // 数値安定性のため、pが0や1に近い場合の処理
    const pSafe = Math.max(1e-10, Math.min(1 - 1e-10, p));
    
    // 二項分布の対数尤度: log L = y*log(p) + (n-y)*log(1-p)
    logLik += dead[i] * Math.log(pSafe) + (total[i] - dead[i]) * Math.log(1 - pSafe);
  }
  
  return -logLik; // 負の対数尤度を返す
}

/**
 * x50を固定してb1を最適化（負の対数尤度を最小化 = 対数尤度を最大化）
 * 
 * @param x50 固定するLD50の対数値（log10(LD50)）
 * @param logDoses 対数用量の配列
 * @param dead 死亡数の配列
 * @param total 総数の配列
 * @param bInitial 初期傾きの推定値
 * @returns 最適化された傾きと負の対数尤度
 */
function optimizeSlopeGivenX50(
  x50: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  bInitial: number,
  qEffect = 0
): { b1: number; nll: number } {
  let b1 = bInitial;
  const maxIterations = 100;
  const tolerance = 1e-6;
  const stepSize = 0.01;
  
  // 最適化範囲（b1が0に近づかないようにする）
  const bMin = -50;
  const bMax = 50;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    // 数値微分で勾配を計算
    const nllCurrent = negativeLogLikelihoodGivenX50(b1, x50, logDoses, dead, total, qEffect);
    const nllPlus = negativeLogLikelihoodGivenX50(b1 + stepSize, x50, logDoses, dead, total, qEffect);
    const gradient = (nllPlus - nllCurrent) / stepSize;
    
    // 勾配が0に近い場合は終了
    if (Math.abs(gradient) < tolerance) {
      break;
    }
    
    // 勾配降下法（負の対数尤度を最小化）
    let b1New = b1 - stepSize * gradient;
    
    // 範囲を制限
    b1New = Math.max(bMin, Math.min(bMax, b1New));
    
    // 収束判定
    if (Math.abs(b1New - b1) < tolerance) {
      b1 = b1New;
      break;
    }
    
    b1 = b1New;
    
    // b1が0に近づかないようにする
    if (Math.abs(b1) < 1e-10) {
      b1 = bInitial;
      break;
    }
  }
  
  const nll = negativeLogLikelihoodGivenX50(b1, x50, logDoses, dead, total, qEffect);
  return { b1, nll };
}

/**
 * 効果量水準（LD50/EC90 等）の log10 用量に対する Profile Likelihood 95% CI
 *
 * 再パラメータ化 eta = qEffect + b1 * (x - xEffect) を用い、xEffect をプロファイル化する。
 *
 * @param xEffectOptimal 最適な log10(用量)（例: log10(LD50), log10(EC90)）
 * @param qEffect リンク上の効果量（50%→0、90%→Φ⁻¹(0.9)）
 */
function calculateLogDoseEffectProfileCI(
  xEffectOptimal: number,
  qEffect: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  bOptimal: number,
  nllOptimal: number
): { lower: number; upper: number } {
  const chi2Critical = 3.841459; // 95%信頼区間、自由度1
  const targetNllDiff = chi2Critical / 2; // 2*(NLL - NLLmin) = chi2 なので NLL - NLLmin = chi2/2
  const tolerance = 1e-4;
  const maxIterations = 100;
  
  // グリッドを設定（x50Optimalの周り）
  const gridRange = Math.max(2, Math.abs(xEffectOptimal) * 0.5 + 1);
  const gridSize = 401;
  const grid = Array.from({ length: gridSize }, (_, i) => 
    xEffectOptimal - gridRange + (2 * gridRange * i) / (gridSize - 1)
  );
  
  // 各グリッドポイントでプロファイル負の対数尤度を計算
  const nllGrid = grid.map((xEffect) => {
    const result = optimizeSlopeGivenX50(xEffect, logDoses, dead, total, bOptimal, qEffect);
    return result.nll;
  });
  
  // 尤度比検定: 2*(NLL - NLLmin) <= chi2Critical となる範囲を探す
  const lrGrid = nllGrid.map(nll => 2 * (nll - nllOptimal));
  
  // CI境界を探す（線形補間）
  const okIndices: number[] = [];
  for (let i = 0; i < gridSize; i++) {
    if (lrGrid[i] <= chi2Critical) {
      okIndices.push(i);
    }
  }
  
  if (okIndices.length === 0) {
    // CIが見つからない場合、グリッド範囲を拡大して再試行
    const extendedGridRange = 5.0;
    const extendedGrid = Array.from({ length: gridSize }, (_, i) => 
      xEffectOptimal - extendedGridRange + (2 * extendedGridRange * i) / (gridSize - 1)
    );
    const extendedNllGrid = extendedGrid.map((xEffect) => {
      const result = optimizeSlopeGivenX50(xEffect, logDoses, dead, total, bOptimal, qEffect);
      return result.nll;
    });
    const extendedLrGrid = extendedNllGrid.map(nll => 2 * (nll - nllOptimal));
    const extendedOkIndices: number[] = [];
    for (let i = 0; i < gridSize; i++) {
      if (extendedLrGrid[i] <= chi2Critical) {
        extendedOkIndices.push(i);
      }
    }
    if (extendedOkIndices.length > 0) {
      const lowerX50 = extendedGrid[extendedOkIndices[0]];
      const upperX50 = extendedGrid[extendedOkIndices[extendedOkIndices.length - 1]];
      return { lower: lowerX50, upper: upperX50 };
    }
    // それでも見つからない場合は、二分探索で探索
    return calculateLogDoseEffectProfileCIBisection(
      xEffectOptimal,
      qEffect,
      logDoses,
      dead,
      total,
      bOptimal,
      nllOptimal,
      targetNllDiff,
      tolerance,
      maxIterations
    );
  }
  
  let lowerX50 = grid[okIndices[0]!]!;
  let upperX50 = grid[okIndices[okIndices.length - 1]!]!;

  if (upperX50 - lowerX50 < 1e-8) {
    return calculateLogDoseEffectProfileCIBisection(
      xEffectOptimal,
      qEffect,
      logDoses,
      dead,
      total,
      bOptimal,
      nllOptimal,
      targetNllDiff,
      tolerance,
      maxIterations
    );
  }

  return { lower: lowerX50, upper: upperX50 };
}

/**
 * 二分探索でLD50のProfile Likelihood CIを計算（フォールバック）
 */
function calculateLogDoseEffectProfileCIBisection(
  xEffectOptimal: number,
  qEffect: number,
  logDoses: number[],
  dead: number[],
  total: number[],
  bOptimal: number,
  nllOptimal: number,
  targetNllDiff: number,
  tolerance: number,
  maxIterations: number
): { lower: number; upper: number } {
  // 下限を探索
  let lowerBound = xEffectOptimal;
  let step = 0.1;
  
  // 下限の初期探索
  for (let i = 0; i < 50; i++) {
    const x50Test = xEffectOptimal - step * (i + 1);
    const result = optimizeSlopeGivenX50(x50Test, logDoses, dead, total, bOptimal, qEffect);
    const nllDiff = result.nll - nllOptimal;
    
    if (nllDiff > targetNllDiff) {
      lowerBound = x50Test;
      break;
    }
  }
  
  // 二分探索で下限を精密化
  let lowerLow = lowerBound - step;
  let lowerHigh = xEffectOptimal;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    const lowerMid = (lowerLow + lowerHigh) / 2;
    const result = optimizeSlopeGivenX50(lowerMid, logDoses, dead, total, bOptimal, qEffect);
    const nllDiff = result.nll - nllOptimal;
    
    if (Math.abs(nllDiff - targetNllDiff) < tolerance) {
      lowerBound = lowerMid;
      break;
    }
    
    if (nllDiff < targetNllDiff) {
      lowerHigh = lowerMid;
    } else {
      lowerLow = lowerMid;
    }
    
    if (Math.abs(lowerHigh - lowerLow) < tolerance) {
      break;
    }
  }
  
  // 上限を探索
  let upperBound = xEffectOptimal;
  
  // 上限の初期探索
  for (let i = 0; i < 50; i++) {
    const x50Test = xEffectOptimal + step * (i + 1);
    const result = optimizeSlopeGivenX50(x50Test, logDoses, dead, total, bOptimal, qEffect);
    const nllDiff = result.nll - nllOptimal;
    
    if (nllDiff > targetNllDiff) {
      upperBound = x50Test;
      break;
    }
  }
  
  // 二分探索で上限を精密化
  let upperLow = xEffectOptimal;
  let upperHigh = upperBound + step;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    const upperMid = (upperLow + upperHigh) / 2;
    const result = optimizeSlopeGivenX50(upperMid, logDoses, dead, total, bOptimal, qEffect);
    const nllDiff = result.nll - nllOptimal;
    
    if (Math.abs(nllDiff - targetNllDiff) < tolerance) {
      upperBound = upperMid;
      break;
    }
    
    if (nllDiff < targetNllDiff) {
      upperLow = upperMid;
    } else {
      upperHigh = upperMid;
    }
    
    if (Math.abs(upperHigh - upperLow) < tolerance) {
      break;
    }
  }
  
  return { lower: lowerBound, upper: upperBound };
}

